import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Trash2,
  UploadCloud,
  X,
  ImageIcon,
  Plus,
  ChevronRight,
  Loader2,
  Check,
} from "lucide-react";
import { adminApi } from "../../lib/adminApi";
import { assetUrl, formatApiErrorDetail } from "../../lib/api";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import ThumbnailCropModal from "../../components/admin/ThumbnailCropModal";

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 120);
}

function formatBytes(n) {
  if (n == null || Number.isNaN(n)) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const emptyForm = {
  name: "",
  slug: "",
  description: "",
  capacity: 0,
  base_price: 0,
  minimum_duration_hours: 4,
  additional_hour_price: null,
  amenities: [],
  status: "inactive",
  display_order: 0,
};

export default function AdminYachtEdit() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const nav = useNavigate();
  const [yacht, setYacht] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [amenityDraft, setAmenityDraft] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmImageDelete, setConfirmImageDelete] = useState(null); // url
  const [uploading, setUploading] = useState(false);
  const [uploadItems, setUploadItems] = useState([]);
  const [thumbUploading, setThumbUploading] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [confirmRemoveThumb, setConfirmRemoveThumb] = useState(false);
  const fileInputRef = useRef(null);
  const thumbFileRef = useRef(null);
  const dragIdx = useRef(null);

  useEffect(() => {
    if (isNew) return;
    adminApi
      .get(`/admin/yachts/${id}`)
      .then((r) => {
        setYacht(r.data);
        setForm({
          name: r.data.name || "",
          slug: r.data.slug || "",
          description: r.data.description || "",
          capacity: r.data.capacity ?? 0,
          base_price: r.data.base_price ?? 0,
          minimum_duration_hours: r.data.minimum_duration_hours ?? 4,
          additional_hour_price: r.data.additional_hour_price ?? null,
          amenities: r.data.amenities || [],
          status: r.data.status || "inactive",
          display_order: r.data.display_order ?? 0,
        });
        setSlugTouched(true);
      })
      .catch((e) => toast.error(formatApiErrorDetail(e)));
  }, [id, isNew]);

  function set(k, v) {
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === "name" && !slugTouched && isNew) next.slug = slugify(v);
      return next;
    });
    setDirty(true);
  }

  function addAmenity() {
    const v = amenityDraft.trim();
    if (!v) return;
    if (form.amenities.includes(v)) {
      setAmenityDraft("");
      return;
    }
    set("amenities", [...form.amenities, v]);
    setAmenityDraft("");
  }
  function removeAmenity(a) {
    set(
      "amenities",
      form.amenities.filter((x) => x !== a)
    );
  }

  async function save() {
    if (saving) return;
    setSaving(true);
    try {
      if (isNew) {
        const payload = { ...form };
        if (!payload.slug) payload.slug = slugify(payload.name);
        const r = await adminApi.post("/admin/yachts", payload);
        toast.success("Yacht created.");
        setDirty(false);
        nav(`/admin/yachts/${r.data.id}`, { replace: true });
      } else {
        const r = await adminApi.patch(`/admin/yachts/${id}`, form);
        setYacht(r.data);
        setDirty(false);
        toast.success("Yacht saved.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setSaving(false);
    }
  }

  async function attemptUpload(file, signal) {
    const fd = new FormData();
    fd.append("files", file, file.name);
    const r = await adminApi.post(`/admin/yachts/${id}/images`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 0,
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      signal,
    });
    return r.data?.results?.[0];
  }

  async function uploadOne(file, index, { isRetry = false } = {}) {
    const setRow = (patch) =>
      setUploadItems((items) =>
        items.map((it, i) => (i === index ? { ...it, ...patch } : it))
      );
    setRow({ status: "uploading", error: null });
    try {
      const res = await attemptUpload(file);
      if (res?.status === "ok") {
        setRow({ status: "ok", finalSize: res.final_size, error: null });
        return true;
      }
      setRow({
        status: "error",
        error: res?.message || "Server rejected the file",
      });
      return false;
    } catch (e) {
      const isNetwork = !e.response;
      // Auto-retry ONCE on transient network failures. Never auto-retry 4xx.
      if (isNetwork && !isRetry) {
        setRow({ status: "retrying", error: "Retrying…" });
        await new Promise((r) => setTimeout(r, 600));
        return uploadOne(file, index, { isRetry: true });
      }
      setRow({
        status: "error",
        error: formatApiErrorDetail(e) || "Network error",
      });
      return false;
    }
  }

  async function runWithConcurrency(files, concurrency) {
    let cursor = 0;
    async function pull() {
      while (true) {
        const i = cursor++;
        if (i >= files.length) return;
        await uploadOne(files[i], i);
      }
    }
    const runners = Array.from(
      { length: Math.min(concurrency, files.length) },
      pull
    );
    await Promise.all(runners);
  }

  async function refreshYacht() {
    try {
      const r = await adminApi.get(`/admin/yachts/${id}`);
      setYacht(r.data);
    } catch {
      // silent — the row-level statuses are the source of truth for the batch
    }
  }

  async function onFilesPicked(fileList) {
    if (!fileList || !fileList.length) return;
    if (isNew) {
      toast.error("Save the yacht first, then upload photos.");
      return;
    }
    const files = Array.from(fileList);
    setUploadItems(
      files.map((f) => ({
        file: f,
        name: f.name,
        size: f.size,
        status: "queued",
        error: null,
      }))
    );
    setUploading(true);
    // Fan out one request per file, concurrency=3. Each request is small
    // (~20 MB max) so no proxy-level body-size limits get hit even on
    // gnarly infra.
    try {
      await runWithConcurrency(files, 3);
    } finally {
      await refreshYacht();
      setUploading(false);
      setUploadItems((items) => {
        const ok = items.filter((it) => it.status === "ok").length;
        const err = items.filter((it) => it.status === "error").length;
        if (err === 0 && ok > 0) {
          toast.success(`Uploaded ${ok} photo${ok !== 1 ? "s" : ""}.`);
        } else if (ok > 0) {
          toast.error(`Uploaded ${ok}, ${err} failed.`);
        } else if (err > 0) {
          toast.error("All uploads failed.");
        }
        return items;
      });
    }
  }

  async function retryOne(index, file) {
    if (!file) return;
    setUploading(true);
    try {
      await uploadOne(file, index);
    } finally {
      await refreshYacht();
      setUploading(false);
    }
  }

  async function onThumbSourcePicked(fileList) {
    if (!fileList || !fileList.length) return;
    if (isNew) {
      toast.error("Save the yacht first, then upload a thumbnail.");
      return;
    }
    const file = fileList[0];
    setThumbUploading(true);
    const fd = new FormData();
    fd.append("file", file, file.name);
    try {
      const r = await adminApi.post(`/admin/yachts/${id}/thumbnail/source`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 0,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      setYacht(r.data);
      setCropOpen(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setThumbUploading(false);
    }
  }

  async function removeThumbnail() {
    try {
      const r = await adminApi.delete(`/admin/yachts/${id}/thumbnail`);
      setYacht(r.data);
      toast.success("Thumbnail removed.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setConfirmRemoveThumb(false);
    }
  }

  async function deleteImage(url) {
    try {
      const r = await adminApi.delete(`/admin/yachts/${id}/images`, {
        data: { image_url: url },
      });
      setYacht(r.data);
      toast.success("Photo removed.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    }
  }

  async function reorderImages(newOrder) {
    // Snapshot the pre-reorder list so we can revert on failure.
    const prevImages = yacht?.images ? [...yacht.images] : null;
    // Optimistic local update — feels instant, tiles don't snap back mid-drag.
    setYacht((y) => (y ? { ...y, images: newOrder } : y));
    try {
      const r = await adminApi.put(`/admin/yachts/${id}/images/reorder`, {
        images: newOrder,
      });
      setYacht(r.data);
    } catch (e) {
      // Revert optimistic state.
      if (prevImages) setYacht((y) => (y ? { ...y, images: prevImages } : y));
      // Use a stable toast id so a duplicate error (StrictMode remount,
      // retry loop) dedupes to a single visible message.
      toast.error(formatApiErrorDetail(e) || "Reorder failed. Try again.", {
        id: "yacht-reorder-error",
      });
      // If the server tells us the sets diverged (a photo was
      // added/removed while dragging), refetch to reconcile silently.
      const code = e?.response?.data?.detail?.error_code;
      if (code === "IMAGE_SET_MISMATCH") {
        await refreshYacht();
      }
    }
  }

  async function archiveYacht() {
    try {
      await adminApi.delete(`/admin/yachts/${id}`);
      toast.success("Yacht archived.");
      nav("/admin/yachts", { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error_code === "YACHT_HAS_ACTIVE_BOOKINGS") {
        toast.error(`Cannot archive: ${detail.booking_count} active booking(s). Cancel them first.`);
      } else {
        toast.error(formatApiErrorDetail(e));
      }
    } finally {
      setConfirmDelete(false);
    }
  }

  const images = yacht?.images || [];
  const publicUrl = useMemo(
    () => (yacht?.slug ? `/yachts/${yacht.slug}` : null),
    [yacht?.slug]
  );

  return (
    <div data-testid="admin-yacht-edit">
      <Link
        to="/admin/yachts"
        className="text-sm text-hbe-ink/70 hover:text-hbe-primary inline-flex items-center gap-1"
      >
        <ArrowLeft size={14} /> Back to yachts
      </Link>

      <div className="mt-4 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="hbe-eyebrow">{isNew ? "New" : "Edit"}</div>
          <h1 className="mt-2 font-serif text-3xl md:text-4xl">
            {isNew ? "New yacht" : yacht?.name || "…"}
          </h1>
        </div>
        {publicUrl && (
          <Link
            to={publicUrl}
            target="_blank"
            className="text-sm text-hbe-primary hover:underline"
            data-testid="ye-view-public"
          >
            View public page →
          </Link>
        )}
      </div>

      <div className="mt-8 space-y-10 pb-32">
        {/* Zero-photo nudge (post-save yachts only) */}
        {!isNew && images.length === 0 && (
          <button
            type="button"
            onClick={() => {
              const el = document.getElementById("ye-photos-section");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="w-full text-left hbe-card p-4 border-l-4 border-hbe-accent bg-hbe-soft/40 hover:bg-hbe-soft transition flex items-center gap-3"
            data-testid="ye-photo-nudge"
          >
            <ImageIcon size={18} className="text-hbe-accent shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-hbe-primary">
                Upload photos to make this yacht visible on the public site
              </div>
              <div className="mt-0.5 text-xs text-hbe-ink/60">
                Yachts without photos still list, but with a "Photos coming
                soon" placeholder. Jump to the Photos section below to add
                some.
              </div>
            </div>
            <ChevronRight size={16} className="text-hbe-accent shrink-0" />
          </button>
        )}

        {/* Content */}
        <Section title="Content">
          <Field label="Name">
            <input
              className="hbe-input"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              data-testid="ye-name"
            />
          </Field>
          <Field label="Slug" hint="URL identifier. Editable — old slugs auto-alias.">
            <input
              className="hbe-input font-mono text-sm"
              value={form.slug}
              onChange={(e) => {
                setSlugTouched(true);
                set("slug", slugify(e.target.value));
              }}
              data-testid="ye-slug"
            />
          </Field>
          <Field label="Description">
            <textarea
              rows={4}
              className="hbe-input resize-y"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              data-testid="ye-description"
            />
          </Field>
          <Field label="Amenities">
            <div className="flex flex-wrap gap-2 mb-2" data-testid="ye-amenities">
              {form.amenities.map((a) => (
                <span
                  key={a}
                  className="inline-flex items-center gap-1 bg-hbe-soft text-hbe-primary text-xs px-3 py-1.5 rounded-full"
                >
                  {a}
                  <button
                    type="button"
                    onClick={() => removeAmenity(a)}
                    className="text-hbe-primary/70 hover:text-hbe-primary"
                    aria-label={`Remove ${a}`}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                className="hbe-input"
                placeholder="e.g. Sun deck"
                value={amenityDraft}
                onChange={(e) => setAmenityDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addAmenity();
                  }
                }}
                data-testid="ye-amenity-input"
              />
              <button
                type="button"
                className="hbe-btn-ghost whitespace-nowrap"
                onClick={addAmenity}
                data-testid="ye-amenity-add"
              >
                <Plus size={14} className="mr-1" /> Add
              </button>
            </div>
          </Field>
        </Section>

        {/* Thumbnail — dedicated public-card image with crop tool */}
        {!isNew && (
          <Section
            title="Thumbnail"
            id="ye-thumbnail-section"
            subtitle={
              <span>
                Shown on yacht cards across the site. Aspect ratio 4:3.
              </span>
            }
          >
            <input
              ref={thumbFileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
              hidden
              onChange={(e) => {
                onThumbSourcePicked(e.target.files);
                e.target.value = "";
              }}
              data-testid="ye-thumb-input"
            />
            {!yacht?.thumbnail_source_url ? (
              <button
                type="button"
                onClick={() => thumbFileRef.current?.click()}
                className="w-full border-2 border-dashed border-hbe-line rounded-xl p-6 md:p-8 text-center bg-hbe-soft/30 hover:bg-hbe-soft/50 transition"
                disabled={thumbUploading}
                data-testid="ye-thumb-empty"
              >
                {thumbUploading ? (
                  <Loader2
                    size={28}
                    className="mx-auto text-hbe-accent animate-spin"
                  />
                ) : (
                  <UploadCloud size={28} className="mx-auto text-hbe-accent" />
                )}
                <div className="mt-2 text-sm font-medium text-hbe-primary">
                  {thumbUploading
                    ? "Uploading…"
                    : "Upload a thumbnail — the image shown on yacht cards across the site."}
                </div>
                <div className="mt-1 text-xs text-hbe-ink/55">
                  We'll help you crop and reposition after upload. JPG · PNG ·
                  WebP · HEIC up to 100 MB.
                </div>
              </button>
            ) : !yacht?.thumbnail_url ? (
              <div
                className="hbe-card p-4 md:p-5 flex items-center gap-4 border-l-4 border-hbe-accent bg-hbe-soft/40"
                data-testid="ye-thumb-awaiting-crop"
              >
                <img
                  src={assetUrl(yacht.thumbnail_source_url)}
                  alt=""
                  className="w-24 h-24 rounded-lg object-cover border border-hbe-line"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-hbe-primary">
                    Set thumbnail crop
                  </div>
                  <div className="text-xs text-hbe-ink/60 mt-0.5">
                    Position the shot before it goes live on cards.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setCropOpen(true)}
                  className="hbe-btn-primary !py-2 !text-[0.85rem] !min-h-[36px]"
                  data-testid="ye-thumb-open-crop"
                >
                  Position
                </button>
              </div>
            ) : (
              <div
                className="grid gap-4 sm:grid-cols-[320px_1fr] items-start"
                data-testid="ye-thumb-active"
              >
                <div className="aspect-[4/3] w-full max-w-[320px] rounded-xl overflow-hidden bg-hbe-soft border border-hbe-line">
                  <img
                    src={assetUrl(yacht.thumbnail_url)}
                    alt={`${yacht.name} thumbnail`}
                    className="w-full h-full object-cover"
                    data-testid="ye-thumb-preview"
                  />
                </div>
                <div className="space-y-3">
                  <p className="text-xs text-hbe-ink/60">
                    This is the exact crop visitors see on the home page and
                    yacht listings.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setCropOpen(true)}
                      className="hbe-btn-primary !py-2 !text-[0.85rem] !min-h-[36px]"
                      data-testid="ye-thumb-reposition"
                    >
                      Reposition
                    </button>
                    <button
                      type="button"
                      onClick={() => thumbFileRef.current?.click()}
                      className="hbe-btn-ghost !py-2 !text-[0.85rem] !min-h-[36px]"
                      disabled={thumbUploading}
                      data-testid="ye-thumb-replace"
                    >
                      {thumbUploading ? "Uploading…" : "Replace source"}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setConfirmRemoveThumb(true)}
                    className="text-xs text-red-600 hover:underline"
                    data-testid="ye-thumb-remove"
                  >
                    Remove thumbnail
                  </button>
                </div>
              </div>
            )}
          </Section>
        )}

        {/* Photos */}
        <Section
          title="Photos"
          id="ye-photos-section"
          subtitle={
            yacht?.image_stats && yacht.image_stats.count > 0 ? (
              <span data-testid="ye-photo-stats">
                {yacht.image_stats.count} photo
                {yacht.image_stats.count !== 1 ? "s" : ""}
                {" • "}
                {formatBytes(yacht.image_stats.total_bytes)} total
              </span>
            ) : null
          }
        >
          <div
            className="border-2 border-dashed border-hbe-line rounded-xl p-8 text-center bg-hbe-soft/30"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              onFilesPicked(e.dataTransfer.files);
            }}
            data-testid="ye-dropzone"
          >
            <UploadCloud size={28} className="mx-auto text-hbe-accent" />
            <p className="mt-2 text-sm text-hbe-ink/70">
              Drop photos here, or{" "}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-hbe-primary underline"
                data-testid="ye-pick-files"
              >
                choose files
              </button>
              . JPG · PNG · WebP · HEIC — up to 100 MB each. Everything is
              auto-converted to WebP.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
              multiple
              hidden
              onChange={(e) => {
                onFilesPicked(e.target.files);
                e.target.value = "";
              }}
              data-testid="ye-file-input"
            />
            {uploading && (
              <div className="mt-3 text-xs text-hbe-primary" data-testid="ye-uploading">
                Uploading…
              </div>
            )}
          </div>

          {uploadItems.length > 0 && (
            <div className="mt-3 space-y-1.5" data-testid="ye-upload-list">
              {(() => {
                const okCount = uploadItems.filter((it) => it.status === "ok").length;
                const errCount = uploadItems.filter((it) => it.status === "error").length;
                const inFlight = uploadItems.filter(
                  (it) => it.status === "uploading" || it.status === "retrying" || it.status === "queued"
                ).length;
                const total = uploadItems.length;
                return (
                  <div
                    className="flex items-center justify-between px-3 py-2 rounded-md bg-hbe-soft/50 border border-hbe-line text-xs"
                    data-testid="ye-upload-summary"
                  >
                    <div className="text-hbe-ink font-medium">
                      {inFlight > 0
                        ? `Uploading ${okCount + 1 <= total ? okCount + 1 : total} of ${total}…`
                        : `${okCount} uploaded${errCount ? `, ${errCount} failed` : ""}`}
                    </div>
                    {inFlight === 0 && (
                      <button
                        type="button"
                        onClick={() => setUploadItems([])}
                        className="text-hbe-ink/60 hover:text-hbe-primary"
                        data-testid="ye-upload-clear"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                );
              })()}
              {uploadItems.map((it, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 text-xs px-3 py-2 rounded-md bg-white border border-hbe-line"
                  data-testid={`ye-upload-row-${i}`}
                  data-status={it.status}
                >
                  {(it.status === "uploading" ||
                    it.status === "retrying" ||
                    it.status === "queued") && (
                    <Loader2 size={14} className="animate-spin text-hbe-accent shrink-0" />
                  )}
                  {it.status === "ok" && (
                    <Check size={14} className="text-green-700 shrink-0" />
                  )}
                  {(it.status === "error" || it.status === "unknown") && (
                    <X size={14} className="text-red-700 shrink-0" />
                  )}
                  <div
                    className="min-w-0 flex-1 truncate text-hbe-ink"
                    title={it.name}
                  >
                    {it.name}
                  </div>
                  <div className="text-hbe-ink/50 whitespace-nowrap">
                    {it.status === "ok" && it.finalSize != null
                      ? `${formatBytes(it.size)} → ${formatBytes(it.finalSize)}`
                      : formatBytes(it.size)}
                  </div>
                  {(it.status === "error" || it.status === "retrying") && (
                    <div
                      className="text-red-700 max-w-[200px] truncate"
                      title={it.error}
                    >
                      {it.error}
                    </div>
                  )}
                  {it.status === "error" && it.file && (
                    <button
                      type="button"
                      onClick={() => retryOne(i, it.file)}
                      className="text-hbe-primary hover:underline whitespace-nowrap"
                      data-testid={`ye-upload-retry-${i}`}
                    >
                      Retry
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {images.length > 0 && (
            <div className="mt-5 grid gap-3 grid-cols-2 md:grid-cols-4">
              {images.map((url, i) => (
                <div
                  key={url}
                  draggable
                  onDragStart={() => {
                    dragIdx.current = i;
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    const from = dragIdx.current;
                    dragIdx.current = null;
                    if (from == null || from === i) return;
                    const next = [...images];
                    const [m] = next.splice(from, 1);
                    next.splice(i, 0, m);
                    reorderImages(next);
                  }}
                  className="relative group aspect-[4/3] rounded-lg overflow-hidden border border-hbe-line cursor-grab active:cursor-grabbing"
                  data-testid={`ye-image-${i}`}
                >
                  <img src={assetUrl(url)} alt="" className="w-full h-full object-cover" />
                  {i === 0 && (
                    <span className="absolute top-2 left-2 bg-hbe-primary text-white text-[10px] uppercase tracking-widest px-2 py-1 rounded-full">
                      Hero
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setConfirmImageDelete(url)}
                    className="absolute top-2 right-2 bg-white/95 text-red-700 rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition"
                    aria-label="Delete photo"
                    data-testid={`ye-image-delete-${i}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
          {images.length === 0 && !isNew && (
            <div className="mt-5 hbe-card p-6 flex items-center gap-3 text-hbe-ink/60">
              <ImageIcon size={18} className="text-hbe-accent" />
              No photos yet. Upload some to make this yacht presentable.
            </div>
          )}
        </Section>

        {/* Details */}
        <Section title="Details">
          <div className="grid gap-5 md:grid-cols-2">
            <Field label="Capacity (guests)">
              <input
                type="number"
                min={0}
                className="hbe-input"
                value={form.capacity}
                onChange={(e) => set("capacity", Number(e.target.value))}
                data-testid="ye-capacity"
              />
            </Field>
            <Field label="Minimum duration (hours)">
              <input
                type="number"
                min={1}
                max={14}
                className="hbe-input"
                value={form.minimum_duration_hours}
                onChange={(e) => set("minimum_duration_hours", Number(e.target.value))}
                data-testid="ye-min-hours"
              />
            </Field>
            <Field label="Base price (USD)" hint={`Covers ${form.minimum_duration_hours || "?"} hours`}>
              <input
                type="number"
                min={0}
                step={50}
                className="hbe-input"
                value={form.base_price}
                onChange={(e) => set("base_price", Number(e.target.value))}
                data-testid="ye-base-price"
              />
            </Field>
            <Field label="Additional hour price (USD)" hint="Leave blank to use base ÷ min hours">
              <input
                type="number"
                min={0}
                step={25}
                className="hbe-input"
                value={form.additional_hour_price ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  set("additional_hour_price", v === "" ? null : Number(v));
                }}
                data-testid="ye-add-price"
              />
            </Field>
          </div>
        </Section>

        {/* Visibility */}
        <Section title="Visibility">
          <Field label="Status">
            <select
              className="hbe-input"
              value={form.status}
              onChange={(e) => set("status", e.target.value)}
              data-testid="ye-status"
            >
              <option value="active">Active — visible on public site</option>
              <option value="inactive">Inactive — hidden from public site</option>
            </select>
          </Field>
          <Field label="Display order" hint="Lower shows first">
            <input
              type="number"
              className="hbe-input"
              value={form.display_order}
              onChange={(e) => set("display_order", Number(e.target.value))}
              data-testid="ye-order"
            />
          </Field>
        </Section>

        {/* Danger */}
        {!isNew && (
          <Section title="Danger zone">
            <div className="hbe-card p-5 border-red-100">
              <div className="text-sm text-hbe-ink/75">
                Archive this yacht. Public pages will 404 immediately.
                Bookings must be cancelled first.
              </div>
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="mt-4 inline-flex items-center rounded-full px-6 py-3 text-sm font-medium bg-red-700 text-white hover:bg-red-800 min-h-[44px]"
                data-testid="ye-archive"
              >
                <Trash2 size={16} className="mr-2" /> Archive yacht
              </button>
            </div>
          </Section>
        )}
      </div>

      {/* Sticky save bar */}
      <div className="fixed bottom-0 inset-x-0 lg:left-[240px] bg-white border-t border-hbe-line z-30">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-3 flex items-center justify-between gap-3">
          <div className="text-xs text-hbe-ink/55" data-testid="ye-dirty">
            {dirty ? "Unsaved changes." : "All changes saved."}
          </div>
          <button
            type="button"
            onClick={save}
            disabled={saving || !form.name}
            className="hbe-btn-primary"
            data-testid="ye-save"
          >
            {saving ? "Saving…" : isNew ? "Create yacht" : "Save changes"}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={archiveYacht}
        title="Archive this yacht?"
        message="It will be hidden from the public site. This can only be undone by an operator via the DB."
        confirmLabel="Archive"
        danger
      />
      <ConfirmDialog
        open={Boolean(confirmImageDelete)}
        onClose={() => setConfirmImageDelete(null)}
        onConfirm={() => {
          const u = confirmImageDelete;
          setConfirmImageDelete(null);
          deleteImage(u);
        }}
        title="Delete this photo?"
        message="It will be removed from the yacht immediately."
        confirmLabel="Delete"
        danger
      />
      <ConfirmDialog
        open={confirmRemoveThumb}
        onClose={() => setConfirmRemoveThumb(false)}
        onConfirm={removeThumbnail}
        title="Remove thumbnail?"
        message="The public yacht card will fall back to the first gallery photo. You can upload a new thumbnail anytime."
        confirmLabel="Remove"
        danger
      />
      <ThumbnailCropModal
        open={cropOpen}
        yacht={yacht}
        onClose={() => setCropOpen(false)}
        onSaved={(updated) => setYacht(updated)}
      />
    </div>
  );
}

function Section({ title, subtitle, children, id }) {
  return (
    <section id={id}>
      <div className="mb-4">
        <h2 className="font-serif text-xl text-hbe-primary">{title}</h2>
        {subtitle && (
          <div className="mt-1 text-xs text-hbe-ink/55">{subtitle}</div>
        )}
      </div>
      <div className="space-y-5">{children}</div>
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="hbe-label">{label}</label>
      {children}
      {hint && <div className="mt-1 text-xs text-hbe-ink/50">{hint}</div>}
    </div>
  );
}
